'use client';
import DataTable, { TableColumn } from '@/components/DataTable';
import { cn } from '@/lib/utils';

export type StatTableRow = {
	id: string;
	label: string;
	jersey?: string | number;
	isTeamRow?: boolean;
	att?: number;
	cmp?: number;
	yds?: number;
	td?: number;
	int?: number;
	rec?: number;
	tkl?: number;
	sack?: number;
	pd?: number;
	fgm?: number;
	fga?: number;
	xpm?: number;
	xpa?: number;
	// Live-stats columns. Optional: each is only shown when some row has a non-zero value, so
	// tables for games entered before the in-house stat tool render exactly as before.
	lng?: number;
	fum?: number;
	lost?: number;
	solo?: number;
	ast?: number;
	tfl?: number;
	ff?: number;
	fr?: number;
	two?: string;
	twoAtt?: number;
	no?: number;
	avg?: string;
	in20?: number;
	kr?: number;
	krYds?: number;
	krTd?: number;
	pr?: number;
	prYds?: number;
	prTd?: number;
};

export type StatCategory = 'passing' | 'rushing' | 'receiving' | 'defense' | 'kicking' | 'punting' | 'returns';

type StatTableProps = {
	title: string;
	category: StatCategory;
	rows: StatTableRow[];
};

const headerClass = 'text-center text-[13px]  font-extrabold leading-[150%]';
const playerCellClass = 'text-[11px]  font-[600] leading-[120%]';
const valueCellClass = 'text-[11px]  font-[600] leading-[120%]';

type StatColConfig = {
	key: keyof StatTableRow;
	header: string;
	align?: 'left' | 'center' | 'right';
	headerClassName?: string;
	cellClassName?: string;
	/** Hide the column unless some row has a non-zero value for `activeKey` (default: `key`). */
	optional?: boolean;
	activeKey?: keyof StatTableRow;
};

const col = (key: keyof StatTableRow, header: string, extra: Partial<StatColConfig> = {}): StatColConfig => ({
	key,
	header,
	align: 'center',
	headerClassName: headerClass,
	cellClassName: valueCellClass,
	...extra,
});

const isActive = (rows: StatTableRow[], key: keyof StatTableRow) =>
	rows.some((row) => {
		const value = row[key];
		return typeof value === 'number' ? value !== 0 : value !== undefined && value !== null && value !== '';
	});

const buildColumns = (category: StatCategory, rows: StatTableRow[]): TableColumn<StatTableRow>[] => {
	const base: TableColumn<StatTableRow>[] = [
		{
			key: 'player',
			header: 'PLAYER',
			align: 'left',
			colClassName: 'w-[100px] md:w-[200px]',
			headerClassName: headerClass + ' text-left pl-4',
			cellClassName: 'flex items-center gap-3 pl-4 ' + playerCellClass + ' ',
			render: (row) => {
				const hasJersey = row.jersey !== undefined && row.jersey !== null && row.jersey !== '';
				const isTeamRow = row.label === 'TEAM';

				return (
					<div className="flex items-center gap-2 overflow-hidden">
						<span
							className={cn(
								`text-[13px] leading-[130%] font-extrabold ${hasJersey ? 'text-otnLime md:text-[16px]' : ''}`,
							)}
						>
							{hasJersey ? row.jersey : isTeamRow ? 'TEAM' : ''}
						</span>
						{hasJersey && !isTeamRow && <span className="overflow-hidden leading-[150%]">{row.label}</span>}
					</div>
				);
			},
		},
	];

	const statColsByCategory: Record<StatCategory, StatColConfig[]> = {
		passing: [
			{ key: 'att', header: 'ATT', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'cmp', header: 'CMP', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'yds', header: 'YDS', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'td', header: 'TD', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'int', header: 'INT', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
		],
		rushing: [
			{ key: 'att', header: 'ATT', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'yds', header: 'YDS', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'td', header: 'TD', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
		],
		receiving: [
			{ key: 'rec', header: 'REC', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'yds', header: 'YDS', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'td', header: 'TD', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
		],
		defense: [
			{ key: 'tkl', header: 'TKL', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'sack', header: 'SACK', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'int', header: 'INT', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'td', header: 'TD', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'pd', header: 'PD', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
		],
		kicking: [
			{ key: 'fgm', header: 'FGM', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'fga', header: 'FGA', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'xpm', header: 'XPM', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
			{ key: 'xpa', header: 'XPA', align: 'center', headerClassName: headerClass, cellClassName: valueCellClass },
		],
		punting: [col('no', 'NO'), col('yds', 'YDS'), col('avg', 'AVG'), col('lng', 'LNG'), col('in20', 'IN20')],
		returns: [
			col('kr', 'KR'),
			col('krYds', 'YDS'),
			col('krTd', 'TD'),
			col('pr', 'PR'),
			col('prYds', 'YDS'),
			col('prTd', 'TD'),
		],
	};

	const optionalColsByCategory: Partial<Record<StatCategory, StatColConfig[]>> = {
		passing: [col('lng', 'LNG', { optional: true })],
		rushing: [
			col('lng', 'LNG', { optional: true }),
			col('fum', 'FUM', { optional: true }),
			col('lost', 'LOST', { optional: true }),
		],
		receiving: [col('lng', 'LNG', { optional: true })],
		defense: [
			col('solo', 'SOLO', { optional: true }),
			col('ast', 'AST', { optional: true }),
			col('tfl', 'TFL', { optional: true }),
			col('ff', 'FF', { optional: true }),
			col('fr', 'FR', { optional: true }),
		],
		kicking: [col('lng', 'LNG', { optional: true }), col('two', '2PT', { optional: true, activeKey: 'twoAtt' })],
	};

	const statCols = [...statColsByCategory[category], ...(optionalColsByCategory[category] ?? [])].filter(
		(c) => !c.optional || isActive(rows, c.activeKey ?? c.key),
	);

	return base.concat(
		statCols.map<TableColumn<StatTableRow>>((col) => ({
			...col,
			colClassName: '',
			render: (row) => {
				const value = (row as any)[col.key] ?? 0;
				return value;
			},
		})),
	);
};

const StatTable = ({ title, category, rows }: StatTableProps) => {
	if (!rows.length) return null;

	const columns = buildColumns(category, rows);

	return (
		<section className="otn-section-border flex flex-col gap-6 bg-black px-6 py-6">
			<div className="border-b-[2px] border-b-[#333] pb-6">
				<h2 className="otn-heading">{title}</h2>
			</div>

			<DataTable columns={columns} rows={rows} getRowKey={(row, index) => row.id || `${row.label}-${index}`} />
		</section>
	);
};

export default StatTable;
